import { format, sub } from 'date-fns';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';

import { PayoutsSortType } from '@/lib/coloc-api/admin';
import { useColocApi } from '@/hooks/use-coloc-api';
import { PayoutPaymentStatus } from '@/features/admin/payouts/types';

type OutgoingStatusOption = {
  status: PayoutPaymentStatus;
  displayValue: string;
};

interface CategoryOption {
  slug: string;
  displayValue: string;
}

const transactionStatus: Array<OutgoingStatusOption> = [
  {
    status: 'unsigned',
    displayValue: t(
      'js.features.admin.transactions.outgoing_transactions_list.transaction_status.unsigned'
    ),
  },
  {
    status: 'pending',
    displayValue: t(
      'js.features.admin.transactions.outgoing_transactions_list.transaction_status.pending'
    ),
  },
  {
    status: 'accepted',
    displayValue: t(
      'js.features.admin.transactions.outgoing_transactions_list.transaction_status.accepted'
    ),
  },
  {
    status: 'rejected',
    displayValue: t(
      'js.features.admin.transactions.outgoing_transactions_list.transaction_status.rejected'
    ),
  },
  {
    status: 'completed',
    displayValue: t(
      'js.features.admin.transactions.outgoing_transactions_list.transaction_status.completed'
    ),
  },
];

const useOutgoingTransactionFilter = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    sub(new Date(format(new Date(), 'yyyy-MM-01')), { months: 0 })
  );
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<OutgoingStatusOption | undefined>(
    transactionStatus.find((status) => status.status === 'unsigned')
  );
  const [transactionStatusQuery, setTransactionStatusQuery] = useState('');
  const [selectedHouse, setSelectedHouse] = useState<{ id: string; name: string } | undefined>(
    undefined
  );
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption | undefined>(undefined);
  const [housesQuery, setHousesQuery] = useState('');

  const { setColumnToSort, sortDirection, selectedColumnToSort, setSortDirection } =
    useColocApi<PayoutsSortType>()!;

  // Set default sorting to created_at
  useEffect(() => {
    setColumnToSort('created_at');
    setSortDirection('descending');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDateRange = useCallback((new_date_range: [Date | null, Date | null]) => {
    setStartDate(new_date_range[0] ?? undefined);
    setEndDate(new_date_range[1] ?? undefined);
  }, []);

  const filteredTransactionStatusArray = useMemo(() => {
    return transactionStatus.filter((statusObject) =>
      statusObject.displayValue.includes(transactionStatusQuery)
    );
  }, [transactionStatusQuery]);

  return {
    startDate,
    endDate,
    setDateRange,
    searchQuery,
    setSearchQuery,
    setColumnToSort,
    sortDirection,
    selectedColumnToSort,
    selectedStatus,
    setSelectedStatus,
    transactionStatusQuery,
    setTransactionStatusQuery,
    filteredTransactionStatusArray,
    selectedHouse,
    setSelectedHouse,
    housesQuery,
    setHousesQuery,
    selectedCategory,
    setSelectedCategory,
  };
};

const GetOutgoingTransactionsListFilterContext = createContext<
  ReturnType<typeof useOutgoingTransactionFilter> | undefined
>(undefined);

const GetOutgoingTransactionsListFilterContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const hook = useOutgoingTransactionFilter();

  return (
    <GetOutgoingTransactionsListFilterContext.Provider value={hook}>
      {children}
    </GetOutgoingTransactionsListFilterContext.Provider>
  );
};

const useGetOutgoingTransactionsListFilterContext = () => {
  const context = useContext(GetOutgoingTransactionsListFilterContext);

  if (context === null) {
    throw new Error(
      'useGetOutgoingTransactionsListFilterContext must be used within a GetOutgoingTransactionsListFilterContextProvider'
    );
  }

  return context;
};

export {
  GetOutgoingTransactionsListFilterContextProvider,
  useGetOutgoingTransactionsListFilterContext,
};
